// @ts-strict-ignore
import { useCallback, useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import fileDownload from 'js-file-download'
import { logError } from '@node-space/utils'
import { rootInitialState } from 'components/FilterBar/context/reducer'
import { useFilterDateValues } from 'components/FilterBar/hooks/useFilterDateValues'
import useFilterMerchant from 'components/FilterBar/hooks/useFilterMerchant'
import useFilterSearch from 'components/FilterBar/hooks/useFilterSearch'
import useFilterStatus from 'components/FilterBar/hooks/useFilterStatus'
import useFilterTag from 'components/FilterBar/hooks/useFilterTag'
import { DateFormat } from 'constants/General'
import { useChannelsPaymentsQuery } from 'hooks/queries/useChannelsPaymentsQuery'
import { getAllChannelsPaymentsCSV } from 'services/PaymentsService'
import { type BaseErrorResponse } from 'types/beneficiaries'
import { type PaymentList } from 'types/payments'

dayjs.extend(utc)

const useChannelsApi = () => {
  const [isExportingCSV, setIsExportingCSV] = useState(false)

  const { safeFromDate, safeToDate, dateValue, setDateValue, onDateSelected, resetDateRange } =
    useFilterDateValues()
  const { status, setStatus, resetStatus } = useFilterStatus()
  const { tag, setTag, resetTag } = useFilterTag()
  const { merchant, setMerchant, resetMerchant } = useFilterMerchant()
  const { search, setSearch, resetSearch, resetIsSearchUsed } = useFilterSearch()

  const [paymentsList, setPaymentsList] = useState<PaymentList>({
    enteredTo: new Date(Date.now()),
    visible: false,
    isTransactionsLoaded: false,
    transactions: [],
    paging: { offset: 0, total: 0, max: 20 },
    pagingMax: 20,
    selectedPayloadOpen: false,
  })

  const { paging, pagingMax } = paymentsList

  const { refetch, isFetching, isError, error } = useChannelsPaymentsQuery(
    {
      paging,
      from: dayjs(safeFromDate).utc().format('YYYY-MM-DD'),
      to: dayjs(safeToDate).utc().format('YYYY-MM-DD'),
      merchant,
      status,
      search,
      tag,
    },
    {
      meta: {
        onSuccess: res => processPayments(res),
        onErrorAdditional: (error: BaseErrorResponse) => {
          console.error('Error on useChannelsPaymentsQuery', error)
        },
      },
    }
  )

  const processPayments = res => {
    const transactions = res?.data || []
    setPaymentsList(prev => ({
      ...prev,
      isTransactionsLoaded: true,
      transactions: [...transactions],
      paging: {
        ...prev?.paging,
        total: res?.paging?.total || 0,
        offset: res?.paging?.offset || 0,
      },
    }))
  }

  useEffect(() => {
    refetch()
  }, [search, refetch])

  useEffect(() => {
    if (
      dateValue?.from?.getTime() === new Date(rootInitialState?.from).getTime() &&
      dateValue?.to?.getTime() === new Date(rootInitialState?.to).getTime() &&
      status === rootInitialState?.status &&
      merchant === rootInitialState?.merchant &&
      tag === rootInitialState?.tag
    ) {
      refetch()
    }
  }, [dateValue?.from, dateValue?.to, status, merchant, tag, refetch])

  useEffect(() => {
    refetch()
  }, [paging.offset, refetch])

  useEffect(() => {
    const isDefaultDates =
      dateValue?.from?.toDateString() === rootInitialState?.from?.toDateString() &&
      dateValue?.to?.toDateString() === rootInitialState?.to?.toDateString()

    if (isDefaultDates) {
      refetch()
    }
  }, [dateValue?.from, dateValue?.to, refetch])

  useEffect(() => {
    return () => {
      resetSearch()
      resetDateRange()
      resetStatus()
      resetMerchant()
      resetTag()
    }
  }, [])

  const exportCSVMutation = useMutation({
    mutationFn: async ({
      fromDate,
      toDate,
      status,
      search,
      merchant,
      tag,
    }: {
      fromDate: string
      toDate: string
      status: string
      search: string
      merchant: string
      tag?: string
    }) => {
      return getAllChannelsPaymentsCSV(fromDate, toDate, status, search, merchant, tag)
    },
    onSuccess: res => {
      const data = res && typeof res === 'object' && 'data' in res ? (res?.data as string) : ''
      const searchParam = search ? `-${search}` : ''
      const fromDate = dateValue?.from
        ? dayjs(dateValue?.from)?.format(DateFormat.YYYY_MM_DD_HH_MM_SS)
        : ''
      const toDate = dateValue?.to
        ? dayjs(dateValue?.to)?.format(DateFormat.YYYY_MM_DD_HH_MM_SS)
        : ''

      try {
        fileDownload(
          data,
          `channels-${fromDate}-${toDate}${searchParam}.csv`,
          'application/octet-stream'
        )
      } catch (err) {
        logError('Error from useChannelApi - handleExportClicked - try FileDownload', err)
      }
      setIsExportingCSV(false)
    },
    onError: err => {
      logError(
        'Error from useChannelApi - handleExportClicked - try getAllChannelsPaymentsCSV',
        err
      )
      setIsExportingCSV(false)
    },
  })

  const handleExportClicked = useCallback(() => {
    setIsExportingCSV(true)
    const fromDate = dateValue?.from
      ? dayjs(dateValue?.from)?.format(DateFormat.YYYY_MM_DD_HH_MM_SS)
      : ''
    const toDate = dateValue?.to ? dayjs(dateValue?.to)?.format(DateFormat.YYYY_MM_DD_HH_MM_SS) : ''

    exportCSVMutation?.mutate({ fromDate, toDate, status, search, merchant, tag })
  }, [dateValue?.from, dateValue?.to, status, search, merchant, tag])

  const onSearchChanged = search => setSearch(search)

  const onPaymentStatusChanged = (status: string) => setStatus(status)

  const onPaymentTagChange = (tag: string) => setTag(tag)

  const onMerchantSelected = useCallback(
    merchant => {
      setMerchant(merchant)
    },
    [dateValue?.from, dateValue?.to, status, tag, merchant, search, paging, pagingMax]
  )

  const onSuccessPayment = useCallback(() => {
    refetch()
  }, [dateValue?.from, dateValue?.to, status, tag, merchant, search, paging, pagingMax])

  const onPageChange = useCallback(
    ({ paging: { offset } }) => {
      setPaymentsList(prev => ({
        ...prev,
        paging: {
          ...paging,
          offset: offset,
          max: pagingMax,
        },
      }))
    },
    [dateValue?.from, dateValue?.to, status, tag, merchant, search, paging, pagingMax]
  )

  const onResetAll = () => {
    setDateValue({
      from: new Date(rootInitialState?.from),
      to: new Date(rootInitialState?.to),
    })
    setStatus(rootInitialState?.status)
    setMerchant(rootInitialState?.merchant)
    setTag(rootInitialState?.tag)
    setSearch(rootInitialState?.search)
    resetIsSearchUsed()
  }

  return {
    executor: refetch,
    isLoading: isFetching,
    isError,
    error,
    data: { paymentsList, dateValue, merchant, search, status, tag, isExportingCSV },
    actions: {
      onDateSelected,
      handleExportClicked,
      onSearchChanged,
      onPaymentStatusChanged,
      onPaymentTagChange,
      onMerchantSelected,
      onSuccessPayment,
      onPageChange,
      onResetAll,
      setPaymentsList,
      showSearchResult: refetch,
    },
  }
}

export default useChannelsApi
